import React, { useRef, useState, useEffect, useCallback } from "react";
import { RichTextEditor, EditorToolbar } from "../../../components/RichTextEditor";
import "./TemplateEditor.css";
import { Box, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { Info } from "@mui/icons-material";

const TemplateEditor = ({
  data,
  setData,
  type = "Manual Customer",
  isToolTip = false,
  labelError = false,
  onBodyBlur,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [editor, setEditor] = useState(null);
  const editorRef = useRef(null);

  const tipsObject = {
    "Reset Password": [
      { "${Url}": "Reset password link" },
      { "${EmailAddress}": "E-mail Address" },
    ],
    Invitation: [
      { "${FirstName}": "Receiver FirstName Name" },
      { "${LastName}": "Receiver LastName Name" },
      { "${EmailAddress}": "Receiver Email Address" },
      { "${PhoneNumber}": "Receiver Phone Number" },
      { "${CompanyName}": "Company Name" },
      { "${companyEmailAddress}": "Company E-mail Address" },
      { "${companyPhoneNumber}": "Company Phone Number" },
      { "${Url}": "Set Password Link" },
    ],
    Quote: [
      { "${FirstName}": "Receiver FirstName Name" },
      { "${LastName}": "Receiver LastName Name" },
      { "${EmailAddress}": "Receiver Email Address" },
      { "${PhoneNumber}": "Receiver Phone Number" },
      { "${CompanyName}": "Company Name" },
      { "${companyEmailAddress}": "Company E-mail Address" },
      { "${companyPhoneNumber}": "Company Phone Number" },
      { "${Title}": "Quote Title" },
      { "${QuoteNumber}": "Quote Number" },
      { "${SubTotal}": "Quote Sub Total" },
      { "${Discount}": "Quote Discount" },
      { "${Tax}": "Quote Tax" },
      { "${Total}": "Quote Total" },
    ],
    Contract: [
      { "${FirstName}": "Receiver FirstName Name" },
      { "${LastName}": "Receiver LastName Name" },
      { "${EmailAddress}": "Receiver Email Address" },
      { "${PhoneNumber}": "Receiver Phone Number" },
      { "${companyName}": "Company Name" },
      { "${companyEmailAddress}": "Company E-mail Address" },
      { "${companyPhoneNumber}": "Company Phone Number" },
      { "${Title}": "Contract Title" },
      { "${ContractNumber}": "Contract Number" },
      { "${SubTotal}": "Contract Sub Total" },
      { "${Discount}": "Contract Discount" },
      { "${Tax}": "Contract Tax" },
      { "${Total}": "Contract Total" },
    ],
    Invoice: [
      { "${FirstName}": "Receiver FirstName Name" },
      { "${LastName}": "Receiver LastName Name" },
      { "${EmailAddress}": "Receiver Email Address" },
      { "${PhoneNumber}": "Receiver Phone Number" },
      { "${companyName}": "Company Name" },
      { "${EmailAddress}": "Company E-mail Address" },
      { "${companyPhoneNumber}": "Company Phone Number" },
      { "${Subject}": "Invoice Title" },
      { "${InvoiceNumber}": "Invoice Number" },
      { "${SubTotal}": "Invoice Sub Total" },
      { "${Discount}": "Invoice Discount" },
      { "${Tax}": "Invoice Tax" },
      { "${Total}": "Invoice Total" },
      { "${PaymentQR}": "QR Code Image" },
    ],
    "Invoice Payment": [
      { "${FirstName}": "Receiver FirstName Name" },
      { "${LastName}": "Receiver LastName Name" },
      { "${EmailAddress}": "Receiver Email Address" },
      { "${PhoneNumber}": "Receiver Phone Number" },
      { "${companyName}": "Company Name" },
      { "${companyEmailAddress}": "Company E-mail Address" },
      { "${companyPhoneNumber}": "Company Phone Number" },
      { "${Total}": "Total Amount" },
      { "${Amount}": "Paid Amount" },
      { "${Method}": "Paid Method" },
    ],
    "Recurring Payment": [
      { "${FirstName}": "Receiver FirstName Name" },
      { "${LastName}": "Receiver LastName Name" },
      { "${EmailAddress}": "Receiver Email Address" },
      { "${PhoneNumber}": "Receiver Phone Number" },
      { "${companyName}": "Company Name" },
      { "${companyEmailAddress}": "Company E-mail Address" },
      { "${companyPhoneNumber}": "Company Phone Number" },
      { "${Amount}": "Paid Amount" },
    ],
    "Request Change": [
      { "${FirstName}": "Receiver FirstName Name" },
      { "${LastName}": "Receiver LastName Name" },
      { "${EmailAddress}": "Receiver Email Address" },
      { "${PhoneNumber}": "Receiver Phone Number" },
      { "${companyName}": "Company Name" },
      { "${companyEmailAddress}": "Company E-mail Address" },
      { "${companyPhoneNumber}": "Company Phone Number" },
      { "${Title}": "Quote Title" },
      { "${QuoteNumber}": "Quote Number" },
      { "${SubTotal}": "Quote Sub Total" },
      { "${Total}": "Quote Total" },
    ],
    Approve: [
      { "${FirstName}": "Receiver FirstName Name" },
      { "${LastName}": "Receiver LastName Name" },
      { "${EmailAddress}": "Receiver Email Address" },
      { "${PhoneNumber}": "Receiver Phone Number" },
      { "${companyName}": "Company Name" },
      { "${companyEmailAddress}": "Company E-mail Address" },
      { "${companyPhoneNumber}": "Company Phone Number" },
      { "${Title}": "Quote Title" },
      { "${QuoteNumber}": "Quote Number" },
      { "${SubTotal}": "Quote Sub Total" },
      { "${Discount}": "Quote Discount" },
      { "${Tax}": "Quote Tax" },
      { "${Total}": "Quote Total" },
    ],
  };

  const ToolTips = () => {
    if (type) {
      const tips = tipsObject[type];
      if (tips && tips?.lenght === 0) return null;

      return (
        <ul>
          {tips.map((tip, index) => {
            const [variable, description] = Object.entries(tip)[0];
            return (
              <li key={index}>
                {variable} - {description}
              </li>
            );
          })}
        </ul>
      );
    } else {
      return null;
    }
  };

  const handleSelectVariable = useCallback((variable) => {
    if (!editorRef.current) return;
    
    const editor = editorRef.current;
    const { from, to } = editor.state.selection;
    
    // Check if the character before the cursor is "$" and remove it
    // This prevents duplicate dollar signs when selecting a variable
    if (from > 0) {
      const textBefore = editor.state.doc.textBetween(Math.max(0, from - 1), from);
      if (textBefore === "$") {
        // Delete the "$" character before inserting the variable
        editor.chain()
          .focus()
          .deleteRange({ from: from - 1, to: from })
          .insertContent(variable)
          .run();
      } else {
        // Insert the variable at the current cursor position
        editor.chain().focus().insertContent(variable).run();
      }
    } else {
      // Insert the variable at the current cursor position
      editor.chain().focus().insertContent(variable).run();
    }
    
    setDropdownOpen(false);
  }, []);

  const handleEditorChange = useCallback((content) => {
    setData(content);
  }, []);

  const handleEditorUpdate = useCallback(({ editor: editorInstance }) => {
    // Check if the last character is "$" to show variable dropdown
    const textContent = editorInstance.getText();
    const lastChar = textContent.slice(-1);

    if (lastChar === "$") {
      const { from, to } = editorInstance.state.selection;
      const coords = editorInstance.view.coordsAtPos(from);
      const editorContainer = document?.querySelector(".rich-text-editor-content");
      const editorRect = editorContainer?.getBoundingClientRect();

      setDropdownPosition({
        top: coords.top + 20,
        left: coords.left,
      });
      setDropdownOpen(true);
    } else {
      setDropdownOpen(false);
    }
  }, []);

  return (
    <>
      <div className="editor-wrapper">
        <div className="d-flex justify-content-between template-editor-label-row">
          <label
            className={`form-control-label fontstylerentr fontfamilysty cs-field-label${
              labelError ? " cs-field-label--error" : " titleecolor"
            }`}
            htmlFor="editor"
            style={{
              fontWeight: "500",
              fontSize: "16px",
            }}
          >
            Body
            <span className="cs-field-label-required" aria-hidden="true">
              {" "}
              *
            </span>
          </label>

          {isToolTip && (
            <Tooltip
              title={
                <Box style={{ fontSize: "14px" }}>
                  You can personalize transaction templates using the following
                  dynamic variables:
                  <ToolTips />
                </Box>
              }
              placement="bottom"
              arrow
            >
              <IconButton>
                <Info />
              </IconButton>
            </Tooltip>
          )}
        </div>

        <div id="editor-toolbar" className="editor-toolbar-wrapper">
          <EditorToolbar editor={editor} />
        </div>

        <div id="editor-container" className="editor-container">
          <RichTextEditor
            content={data}
            onChange={handleEditorChange}
            onUpdate={handleEditorUpdate}
            onBlur={onBodyBlur}
            onReady={(editorInstance) => {
              editorRef.current = editorInstance;
              setEditor(editorInstance);
            }}
            placeholder="Start typing..."
          />

          {dropdownOpen && (
            <Menu
              open={dropdownOpen}
              anchorReference="anchorPosition"
              anchorPosition={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
              }}
              onClose={() => setDropdownOpen(false)}
            >
              {tipsObject[type]?.map((tip, index) => {
                const [variable] = Object.keys(tip);
                return (
                  <MenuItem
                    key={index}
                    onClick={() => handleSelectVariable(variable)}
                  >
                    {variable}
                  </MenuItem>
                );
              })}
            </Menu>
          )}
        </div>
      </div>
    </>
  );
};

export default TemplateEditor;
